import { PERMISSIONS as P } from "../../constants/permissions";

// How the PLM Role Management screen lays out permissions: one section per
// area of the app, each permission with a plain-English label and a line on
// what it actually allows. Any PLM permission not listed here still shows,
// under "Other", so a new one is never hidden.
export const PLM_PERMISSION_SECTIONS = [
  {
    title: "Projects",
    description: "Creating, viewing and running projects.",
    permissions: [
      { value: P.PROJECT_VIEW_ALL, label: "View all projects", hint: "Every project in the company, plus the Home dashboard" },
      { value: P.PROJECT_VIEW_ASSIGNED, label: "View assigned projects", hint: "Only projects the user is allocated to or has a stage in" },
      { value: P.PROJECT_CREATE, label: "Create projects" },
      { value: P.PROJECT_UPDATE_STATUS, label: "Change project status" },
      { value: P.PROJECT_ASSIGN_USERS, label: "Assign people", hint: "Allocate people to projects and assign them to stages" },
      { value: P.PROJECT_DELETE, label: "Delete & archive projects", hint: "Delete projects, and see and restore archived ones" },
    ],
  },
  {
    title: "Tasks",
    description: "My Tasks — to-dos, bugs and approvals, on their own or linked to projects.",
    permissions: [
      { value: P.TASK_VIEW, label: "Use My Tasks", hint: "Open My Tasks and work on tasks the user created, is assigned or collaborates on" },
      { value: P.TASK_CREATE, label: "Create tasks", hint: "New tasks and subtasks" },
      { value: P.TASK_ASSIGN, label: "Assign to others", hint: "Without this, tasks can only be assigned to yourself" },
      { value: P.TASK_VIEW_ALL, label: "View all tasks", hint: "Every task in the company, plus team Task Analytics" },
      { value: P.TASK_DELETE_ANY, label: "Delete any task", hint: "Delete tasks created by someone else" },
    ],
  },
  {
    title: "Invoicing",
    description: "Billing completed projects. Only shown when invoicing is turned on.",
    permissions: [
      { value: P.INVOICING_VIEW, label: "View invoicing", hint: "The Invoicing page" },
      { value: P.INVOICING_MANAGE, label: "Manage invoicing", hint: "Flag projects for invoicing and mark them invoiced" },
    ],
  },
  {
    title: "Workflows",
    description: "The stage templates projects are built from.",
    permissions: [
      { value: P.WORKFLOW_MANAGE_VIEW, label: "View workflows" },
      { value: P.WORKFLOW_MANAGE_CREATE, label: "Create workflows" },
      { value: P.WORKFLOW_MANAGE_EDIT, label: "Edit workflows", hint: "Includes the Workflows admin page" },
      { value: P.WORKFLOW_MANAGE_DELETE, label: "Delete workflows" },
      { value: P.WORKFLOW_MANAGE_ASSIGN, label: "Assign workflows" },
      { value: P.WORKFLOW_IMPORT, label: "Import workflows", hint: "Load a workflow from a JSON file" },
      { value: P.WORKFLOW_EXPORT, label: "Export workflows", hint: "Download workflows and reports as JSON or CSV" },
    ],
  },
  {
    title: "Scheduling",
    description: "Working hours, holidays and who works on what next.",
    permissions: [
      { value: P.SCHEDULE_VIEW_OWN, label: "View own schedule", hint: "The My Schedule page" },
      { value: P.SCHEDULE_VIEW_ALL, label: "View everyone's schedule", hint: "The Planner across the whole team" },
      { value: P.SCHEDULE_SET_PRIORITY, label: "Set priorities", hint: "Reorder people's task queues" },
      { value: P.SCHEDULE_CONFIGURE_USER, label: "Configure people's schedules", hint: "Individual working hours and leave" },
      { value: P.SCHEDULE_CONFIGURE_ORG, label: "Configure company schedule", hint: "Company working hours and holidays" },
    ],
  },
  {
    title: "Team & Time",
    description: "Activity, workload and logged hours.",
    permissions: [
      { value: P.TEAM_ACTIVITY_VIEW_OWN, label: "View own activity", hint: "The Team page, limited to yourself" },
      { value: P.TEAM_ACTIVITY_VIEW_PROJECT, label: "View project activity", hint: "Activity of people on your projects" },
      { value: P.TEAM_ACTIVITY_VIEW_ALL, label: "View all activity", hint: "Activity of everyone in the company" },
      { value: P.WORKLOAD_VIEW_OWN, label: "View own workload" },
      { value: P.WORKLOAD_VIEW_TEAM, label: "View team workload" },
      { value: P.WORKLOAD_VIEW_ALL, label: "View all workload" },
      { value: P.TIME_LOG_VIEW_OWN, label: "View own time logs" },
      { value: P.TIME_LOG_VIEW_PROJECT, label: "View project time logs", hint: "Hours logged on projects you can see" },
      { value: P.TIME_LOG_VIEW_ALL, label: "View all time logs" },
      { value: P.TIME_LOG_EDIT, label: "Edit time logs", hint: "Correct hours other people logged" },
    ],
  },
  {
    title: "Users",
    description: "People in the company.",
    permissions: [
      { value: P.USER_VIEW_ALL, label: "View users", hint: "The Users admin page" },
      { value: P.USER_CREATE, label: "Add users", hint: "Invite new people" },
      { value: P.USER_UPDATE, label: "Edit users" },
      { value: P.USER_DELETE, label: "Deactivate users" },
    ],
  },
  {
    title: "Roles",
    description: "This screen — who can do what.",
    permissions: [
      { value: P.ROLE_VIEW, label: "View roles" },
      { value: P.ROLE_CREATE, label: "Create roles" },
      { value: P.ROLE_UPDATE, label: "Edit role permissions" },
      { value: P.ROLE_DELETE, label: "Delete roles" },
      { value: P.ROLE_ASSIGN, label: "Assign roles", hint: "Give users roles" },
    ],
  },
];
